import { createStyles, makeStyles, Snackbar, Theme } from '@material-ui/core';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import MaterialTable, { Column } from 'material-table';
import React, { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import Loading from '../../components/Loading';
import { getStations, showSnackbar } from '../../store/actions/station';
import { CreateStationState } from '../../store/reducers/createStation';
import { StationState } from '../../store/reducers/station';
import { Station } from '../../models/Station';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    margin: {
      marginLeft: 'auto',
      marginBottom: theme.spacing(1),
    },
    extendedIcon: {
      marginRight: theme.spacing(1),
    },
    dialogFormActionContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'flex-end',
    },
  })
);

interface RootState {
  station: StationState;
  createStation: CreateStationState;
}

type Props = RouteComponentProps;

const Stations: React.FC<Props> = ({ history }) => {
  const columns: Column<Station>[] = [
    {
      title: 'Station Name',
      field: 'name',
      searchable: true,
      filtering: false,
    },
    {
      title: 'Company Name',
      field: 'companyName',
      searchable: true,
      filtering: false,
    },
    { title: 'Address', field: 'address', searchable: false, filtering: false },
    {
      title: 'Postcode',
      field: 'postcode',
      searchable: false,
      filtering: false,
    },
    {
      title: 'State',
      field: 'state',
      searchable: false,
      lookup: {
        Perlis: 'Perlis',
        Kedah: 'Kedah',
        Penang: 'Penang',
        Kelantan: 'Kelantan',
        Terengganu: 'Terengganu',
        Pahang: 'Pahang',
        Perak: 'Perak',
        Selangor: 'Selangor',
        Kuala: 'Kuala',
        Negeri: 'Negeri',
        Melaka: 'Melaka',
        Johor: 'Johor',
        Sabah: 'Sabah',
        Sarawak: 'Sarawak',
        Labuan: 'Labuan',
        Putrajaya: 'Putrajaya',
      },
    },
  ];
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const stations = useSelector(
    (state: RootState) => state.station.stations,
    (left, right) => {
      return isEqual(left, right);
    }
  );

  const {
    loading: isCreateStationLoading,
    error: createStationError,
    shouldShowSnackbar,
  } = useSelector((state: RootState) => state.createStation);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchStations = async () => {
      dispatch(getStations());
    };
    fetchStations();
  }, [stations]);

  const snackbarDispatch = useDispatch();

  useEffect(() => {
    if (shouldShowSnackbar) {
      setOpenSnackbar(true);
      dispatch(getStations());
    }
    return () => {
      snackbarDispatch(showSnackbar(false));
    };
  }, [shouldShowSnackbar]);

  const rowClickHandler = (
    event: React.MouseEvent<Element, MouseEvent> | undefined,
    rowData:
      | {
          id: string;
          name: string;
          address: string;
        }
      | undefined
  ) => {
    history.push('/stations/' + rowData!!.id);
  };

  const getPageSize = (): number => {
    if (stations.length < 5) {
      return 5;
    } else if (stations.length < 10) {
      return 10;
    } else if (stations.length < 15) {
      return 15;
    } else {
      return 20;
    }
  };

  return (
    <div>
      <div style={{ maxWidth: '100%' }}>
        <MaterialTable
          key={stations.length}
          localization={{
            toolbar: { searchPlaceholder: 'Search Station/Company' },
          }}
          options={{
            search: true,
            pageSize: getPageSize(),
            filtering: true,
            pageSizeOptions: [],
          }}
          columns={columns}
          data={stations.map((station) => {
            return {
              name: station.name,
              companyName: station.companyName,
              companyId: station.companyId,
              address: station.address,
              postcode: station.postcode,
              state: station.state,
              id: station.id,
            };
          })}
          title="Stations"
          onRowClick={(event, rowData) => rowClickHandler(event, rowData)}
        />
      </div>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          {createStationError !== ''
            ? createStationError
            : 'Station is successfully created!'}
        </Alert>
      </Snackbar>
      <Loading isLoading={isCreateStationLoading} />
    </div>
  );
};

function Alert(props: AlertProps) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default Stations;
